// A condition visit's status, inspection date and links, on the device (the
// API's visit-status.ts and visit-links.ts decide; this file asks and takes
// the answer). Status changes and links need signal: they go straight to the
// API, never through the outbox, and the visit's copy takes CRM's answer.
import {
  addReportContactApi,
  changeVisitStatusApi,
  chooseReportContactApi,
  saveVisitLinksApi,
  signVisitApi,
  type ClientContactFromApi,
  type NewContactToApi,
  type VisitChangeFromApi,
  type VisitLinksToApi,
} from './api';
import { patchFields, takeServerChange, type DeviceRecord } from './device-db';
import { inspectedOnFor } from './visit-steps';

/** Correct the inspection date (it starts as when the report was started). Saved like any edit, offline too. */
export const setInspectedOn = (visit: DeviceRecord, day: string): Promise<void> => patchFields(visit.id, { hig_inspectedon: inspectedOnFor(day) });

const take = async (visit: DeviceRecord, change: VisitChangeFromApi) => takeServerChange(visit.id, change.fields, change.etag);

/** Complete, validate or reopen the visit; throws the API's error (409 UNRATED_AREAS with `areas`) for the screen to show. */
export async function changeVisitStatus(visit: DeviceRecord, change: 'complete' | 'validate' | 'reopen', force = false): Promise<void> {
  const changed = await changeVisitStatusApi(visit.id, change, force);
  await take(visit, changed);
  // Validating wrote each building's condition: the buildings take it and their new etags.
  for (const building of changed.buildings ?? []) await takeServerChange(building.id, building.fields, building.etag);
}

/** Move the visit to another of the client's centres, set or clear the visit before it, or set the centre's year built. */
export async function changeVisitLinks(visit: DeviceRecord, change: VisitLinksToApi): Promise<void> {
  await take(visit, await saveVisitLinksApi(visit.id, change));
}

/** Address the condition report to one of the client's contacts; null goes back to the job's contact. */
export async function chooseReportContact(visit: DeviceRecord, contactId: string | null): Promise<void> {
  await take(visit, await chooseReportContactApi(visit.id, contactId));
}

/** Add a contact to the client's account and address the report to it; throws 409 CONTACT_EXISTS with the contact. */
export async function addReportContact(visit: DeviceRecord, contact: NewContactToApi): Promise<ClientContactFromApi> {
  const added = await addReportContactApi(visit.id, contact);
  await take(visit, added);
  return added.contact;
}

/** Save a signature for the visit (needs signal); the visit's copy takes the signed columns. */
export async function signVisit(visit: DeviceRecord, who: 'client' | 'technician', name: string, png: Blob): Promise<void> {
  await take(visit, await signVisitApi(visit.id, who, name, png));
}
